import { KeyRound, Search } from 'lucide-react';
import { useState } from 'react';

import { useUsuario } from '@/lib/auth/contexto';
import { podeNaEmpresa } from '@/lib/auth/permissoes';
import { useEscopoEmpresas } from '@/lib/escopo';
import { juntarPaginas } from '@/lib/paginacao';
import { EstadoVazio } from '@/ui/components/estado-vazio';
import { TabelaDados } from '@/ui/components/tabela-dados';
import { CabecalhoPagina } from '@/ui/patterns/cabecalho-pagina';
import { PaginaLista } from '@/ui/patterns/pagina-lista';
import { Input } from '@/ui/primitives/input';
import { Select } from '@/ui/primitives/select';

import { AcoesChave } from './acoes-chave';
import { type StatusChave, useChavesPendentes } from './api';
import { colunasChaves } from './colunas';

const STATUS: { valor: StatusChave; rotulo: string }[] = [
  { valor: 'PENDENTE', rotulo: 'Aguardando XML' },
  { valor: 'OBTIDA', rotulo: 'XML recebido' },
  { valor: 'INVALIDA', rotulo: 'Chave inválida' },
];

export function PaginaChaves() {
  const usuario = useUsuario();
  const escopo = useEscopoEmpresas();
  const [status, setStatus] = useState<StatusChave>('PENDENTE');
  const [busca, setBusca] = useState('');
  const consulta = useChavesPendentes(escopo, status, busca.trim());
  const chaves = juntarPaginas(consulta.data);

  return (
    <PaginaLista
      cabecalho={
        <CabecalhoPagina
          titulo="Chaves aguardando XML"
          descricao="Notas que a SEFAZ diz que existem, mas cujo XML ainda não chegou. Cada uma se resolve sozinha quando o XML é importado."
        />
      }
      filtros={
        <div className="flex flex-wrap gap-2">
          <label className="relative min-w-64 flex-1">
            <span className="sr-only">Buscar chaves</span>
            <Search
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-texto-fraco"
              aria-hidden
            />
            <Input
              value={busca}
              onChange={(e) => {
                setBusca(e.target.value);
              }}
              placeholder="Chave, número ou emitente"
              className="pl-9"
            />
          </label>
          <Select
            aria-label="Situação da chave"
            className="w-48"
            opcoes={STATUS}
            valor={status}
            aoMudar={(v) => {
              setStatus(v as StatusChave);
            }}
          />
        </div>
      }
      lista={
        <TabelaDados
          rotulo="Chaves pendentes"
          className="flex-1"
          colunas={colunasChaves}
          dados={chaves}
          idLinha={(c) => c.id}
          carregando={consulta.isPending}
          acoesLinha={(c) => (
            <AcoesChave
              chave={c}
              podeBuscar={podeNaEmpresa(usuario, c.empresaId, 'documentos.captura.executar')}
            />
          )}
          aoChegarAoFim={() => {
            if (consulta.hasNextPage && !consulta.isFetchingNextPage) void consulta.fetchNextPage();
          }}
          vazio={
            <EstadoVazio
              icone={<KeyRound />}
              titulo={status === 'PENDENTE' ? 'Nenhuma nota aguardando XML' : 'Nada por aqui'}
              texto="Importe o relatório de NF-e destinadas da SEFAZ para saber quais notas existem antes dos XMLs."
            />
          }
        />
      }
    />
  );
}
